import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthFacade } from '../auth/+store/auth.facade';
import {
  Color,
  ICustomerUser,
  IPatientPortalLayout
} from '@care-portals/api-interfaces';
import { PortalsColors } from '@appgen-health-crm/data';

@Injectable({ providedIn: 'root' })
export class BrandService {
  PRODUCT_LINK = '/products';
  CONTACT_US = '/contact-us';
  user: ICustomerUser;
  private loadedFonts: Set<string> = new Set();

  constructor(
    private titleService: Title,
    private readonly authFacade: AuthFacade
  ) {
    this.authFacade.user$.subscribe(user => {
      this.user = user;
    });
  }

  layout: IPatientPortalLayout;
  setBrand(layout: IPatientPortalLayout) {
    this.layout = layout;
    const brand = layout.brand;
    const theme = layout.theme || 'portals';
    const bodyElem = document.getElementsByTagName('body')[0];

    bodyElem.classList.forEach(res =>
      document.getElementsByTagName('body')[0].classList.remove(res)
    );

    const getColor = (color: Color) => {
      if (PortalsColors[color]) {
        return PortalsColors[color];
      }
      return color;
    };

    // classList tokens cannot contain whitespace (e.g. brand "Nu1 Health"),
    // and a throw here aborts the whole layout load, leaving a blank page.
    const toClassToken = (value: string) => value.trim().replace(/\s+/g, '-');
    bodyElem.classList.add(
      ...[theme, brand].filter(Boolean).map(toClassToken)
    );

    if (layout.colors?.mainFontFamily) {
      this.loadGoogleFont(layout.colors.mainFontFamily);
    }

    if (layout.colors) {
      Object.entries(layout.colors).forEach(([key, value]) => {
        if (value) {
          const cssVarName = this.toCssVariableName(key);
          const cssValue = key.toLowerCase().includes('color')
            ? getColor(value)
            : value;
          bodyElem.style.setProperty(cssVarName, cssValue);
        }
      });
    }

    const name = this.layout.title;
    this.titleService.setTitle(`${name}: Patient Portal`);
    document
      .querySelector(`link[rel*='icon']`)
      .setAttribute('href', `assets/favicons/${theme}-favicon.ico`);
    return brand;
  }

  private toCssVariableName(camelCase: string): string {
    return '--' + camelCase.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  private loadGoogleFont(fontFamily: string): void {
    const fonts = fontFamily
      .split(',')
      .map(font => font.trim().replace(/['",]/g, ''));

    fonts.forEach(font => {
      if (this.loadedFonts.has(font) || this.isSystemFont(font)) {
        return;
      }

      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${font.replace(
        /\s+/g,
        '+'
      )}:wght@300;400;500;600;700&display=swap`;
      link.rel = 'stylesheet';

      link.addEventListener('load', () => {
        this.loadedFonts.add(font);
      });

      link.addEventListener('error', () => {
        console.warn(`Failed to load font: ${font}`);
      });

      document.head.appendChild(link);
    });
  }

  private isSystemFont(font: string): boolean {
    const systemFonts = [
      'Arial',
      'Helvetica',
      'Times New Roman',
      'Times',
      'Courier New',
      'Courier',
      'Verdana',
      'Georgia',
      'Palatino',
      'Garamond',
      'Bookman',
      'Tahoma',
      'Trebuchet MS',
      'Arial Black',
      'Impact'
    ];
    return systemFonts.includes(font);
  }

  getShopUrl() {
    if (!this.layout?.shopUrl) {
      console.error('shopUrl is not setup in org config', this.layout);
      throw new Error('shopUrl is not setup in org config');
    }
    return this.layout?.shopUrl;
  }

  goToShop() {
    window.open(this.getShopUrl());
  }

  goToContactUs() {
    window.open(this.getShopUrl() + this.CONTACT_US, '_blank');
  }

  openProductPage(productName: string) {
    window.open(
      `${this.getShopUrl()}${this.PRODUCT_LINK}/${productName}`,
      '_blank'
    );
  }

  clickTopAction() {
    const renderedLink = this.layout?.topAction?.link?.replace(
      '${email}',
      this.user?.email
    );
    if (renderedLink) {
      window.open(renderedLink);
    }
  }

  clickLogo() {
    if (this.layout?.logo?.link) {
      window.open(this.layout.logo.link);
    } else {
      window.location.href = '/';
    }
  }

  setupGtm(id?: string, url?: string) {
    // No container configured — do not inject a default GTM snippet.
    if (!id) {
      return;
    }
    const w = window;
    const d = document;
    const s = 'script';
    const l = 'dataLayer';
    const u = url || 'https://www.googletagmanager.com/gtm.js';
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s) as HTMLScriptElement,
      dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = u + '?id=' + id + dl;
    f.parentNode.insertBefore(j, f);
  }
}
